import React, { useContext, useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, ClipboardList, Building2, Mail, Calendar, 
  User, AlertCircle, Loader2, ListChecks, AlertTriangle, CheckCircle2, FileText, Download
} from 'lucide-react';
import AuthContext from '../../../../../context/AuthContext';
import superAdminAuditService from '../../../../../services/audit_services/superAdminAuditService';
import superAdminAttachmentService from '../../../../../services/audit_services/superAdminAttachmentService';

export default function AllAuditPlanDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { authTokens } = useContext(AuthContext);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [planData, setPlanData] = useState(null);

  useEffect(() => {
    if (id) {
      fetchPlanDetails();
    }
  }, [id]);

  const fetchPlanDetails = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const data = await superAdminAuditService.getAuditPlanDetails(authTokens.access, id);
      setPlanData(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadAttachment = async (attachmentId, fileName) => {
    try {
      const result = await superAdminAttachmentService.downloadAuditPlanAttachment(authTokens.access, attachmentId);
      
      if (!result.success) {
        setError(result.error || 'Erreur lors du téléchargement');
      }
    } catch (error) {
      console.error('Error downloading attachment:', error);
      setError('Erreur lors du téléchargement');
    }
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      'pending': 'bg-gray-100 text-gray-700',
      'in_progress': 'bg-blue-100 text-blue-700',
      'completed': 'bg-green-100 text-green-700',
      'closed': 'bg-red-100 text-red-700',
      'achieved': 'bg-purple-100 text-purple-700'
    };

    const colorClass = statusColors[status?.toLowerCase()] || 'bg-gray-100 text-gray-700';

    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${colorClass}`}>
        {status === 'pending' ? 'En attente' : 
         status === 'in_progress' ? 'En cours' : 
         status === 'completed' ? 'Terminé' : 
         status === 'closed' ? 'Fermé' : 
         status === 'achieved' ? 'Réalisé' : status}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="p-5 sm:p-6 max-w-7xl mx-auto w-full">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 text-blue-600 animate-spin" />
          <span className="ml-3 text-gray-600">Chargement des détails...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-5 sm:p-6 max-w-7xl mx-auto w-full">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 text-red-600" />
            <span className="ml-2 text-sm text-red-700">{error}</span>
          </div>
        </div>
      </div>
    );
  }

  if (!planData) {
    return (
      <div className="p-5 sm:p-6 max-w-7xl mx-auto w-full">
        <div className="text-center py-12">
          <p className="text-gray-500">Aucune donnée trouvée</p>
        </div>
      </div>
    );
  }

  const { plan, organization, audit_program, created_by, last_updated_by, parties, sections, risks, statistics } = planData;

  return (
    <div className="p-5 sm:p-6 max-w-7xl mx-auto w-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <button
            onClick={() => navigate('/admin-panel/audit-plans')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="h-5 w-5 text-gray-600" />
          </button>
          <div>
            <h1 className="text-lg font-semibold text-gray-900">{plan.title}</h1>
            <p className="text-xs text-gray-500">Détails du plan d'audit</p>
          </div>
        </div>
        
        <div className="flex items-center space-x-3">
          {getStatusBadge(plan.status)}
        </div>
      </div>

      <div className="space-y-6">
        {/* Plan Information Card */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center mb-4">
            <ClipboardList className="h-5 w-5 text-gray-600 mr-2" />
            <h2 className="text-sm font-semibold text-gray-900">Informations du plan</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <div className="space-y-3">
                <div>
                  <label className="text-xs font-medium text-gray-500">Titre</label>
                  <p className="text-sm text-gray-900">{plan.title}</p>
                </div>
                {plan.reference && (
                  <div>
                    <label className="text-xs font-medium text-gray-500">Référence</label>
                    <p className="text-sm text-gray-900">{plan.reference}</p>
                  </div>
                )}
                {plan.location && (
                  <div>
                    <label className="text-xs font-medium text-gray-500">Localisation</label>
                    <p className="text-sm text-gray-900">{plan.location}</p>
                  </div>
                )}
                {plan.objectives && (
                  <div>
                    <label className="text-xs font-medium text-gray-500">Objectifs</label>
                    <p className="text-sm text-gray-900">{plan.objectives}</p>
                  </div>
                )}
                {plan.scope && (
                  <div>
                    <label className="text-xs font-medium text-gray-500">Périmètre</label>
                    <p className="text-sm text-gray-900">{plan.scope}</p>
                  </div>
                )}
                {plan.note && (
                  <div>
                    <label className="text-xs font-medium text-gray-500">Note</label>
                    <p className="text-sm text-gray-900">{plan.note}</p>
                  </div>
                )}
              </div>
            </div>
            
            <div>
              <div className="space-y-3">
                <div>
                  <label className="text-xs font-medium text-gray-500">Statut</label>
                  <div className="mt-1">
                    {getStatusBadge(plan.status)}
                  </div>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500">Publié</label>
                  <p className="text-sm text-gray-900">{plan.is_published ? 'Oui' : 'Non'}</p>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500">Libéré</label>
                  <p className="text-sm text-gray-900">{plan.is_released ? 'Oui' : 'Non'}</p>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500">Créé le</label>
                  <div className="flex items-center">
                    <Calendar className="h-3 w-3 text-gray-400 mr-1" />
                    <p className="text-sm text-gray-900">
                      {new Date(plan.created_at).toLocaleDateString('fr-FR')}
                    </p>
                  </div>
                </div>
                {plan.release_date && (
                  <div>
                    <label className="text-xs font-medium text-gray-500">Date de libération</label>
                    <div className="flex items-center">
                      <Calendar className="h-3 w-3 text-gray-400 mr-1" />
                      <p className="text-sm text-gray-900">
                        {new Date(plan.release_date).toLocaleDateString('fr-FR')}
                      </p>
                    </div>
                  </div>
                )}
                {plan.start_date && (
                  <div>
                    <label className="text-xs font-medium text-gray-500">Date de début</label>
                    <div className="flex items-center">
                      <Calendar className="h-3 w-3 text-gray-400 mr-1" />
                      <p className="text-sm text-gray-900">
                        {new Date(plan.start_date).toLocaleDateString('fr-FR')}
                      </p>
                    </div>
                  </div>
                )}
                {plan.end_date && (
                  <div>
                    <label className="text-xs font-medium text-gray-500">Date de fin</label>
                    <div className="flex items-center">
                      <Calendar className="h-3 w-3 text-gray-400 mr-1" />
                      <p className="text-sm text-gray-900">
                        {new Date(plan.end_date).toLocaleDateString('fr-FR')}
                      </p>
                    </div>
                  </div>
                )}
                {plan.achieved_date && (
                  <div>
                    <label className="text-xs font-medium text-gray-500">Date de réalisation</label>
                    <div className="flex items-center">
                      <Calendar className="h-3 w-3 text-gray-400 mr-1" />
                      <p className="text-sm text-gray-900">
                        {new Date(plan.achieved_date).toLocaleDateString('fr-FR')}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Parties within Plan Info */}
          {parties && parties.length > 0 && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <div className="flex items-center mb-3">
                <User className="h-4 w-4 text-gray-600 mr-2" />
                <h3 className="text-xs font-semibold text-gray-900">Parties prenantes ({parties.length})</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {parties.map((party) => (
                  <div key={party.id} className="p-3 rounded-lg border border-gray-200 bg-gray-50">
                    <div className="flex items-center space-x-2 mb-1">
                      <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                        party.role === 'auditor' ? 'bg-blue-100 text-blue-700' :
                        party.role === 'auditee' ? 'bg-green-100 text-green-700' :
                        party.role === 'approver' ? 'bg-purple-100 text-purple-700' :
                        party.role === 'participant' ? 'bg-gray-100 text-gray-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {party.role === 'auditor' ? 'Auditeur' :
                         party.role === 'auditee' ? 'Audité' :
                         party.role === 'approver' ? 'Approbateur' :
                         party.role === 'participant' ? 'Participant' : party.role}
                      </span>
                    </div>
                    <p className="text-sm font-medium text-gray-900">
                      {party.user.full_name || `${party.user.first_name} ${party.user.last_name}`.trim() || 'Inconnu'}
                    </p>
                    {party.user.email && (
                      <div className="flex items-center mt-1">
                        <Mail className="h-3 w-3 text-gray-400 mr-1" />
                        <p className="text-xs text-gray-600">{party.user.email}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Organization Card */}
        {organization && (
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center mb-4">
              <Building2 className="h-5 w-5 text-gray-600 mr-2" />
              <h2 className="text-sm font-semibold text-gray-900">Organisation</h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <div className="space-y-3">
                  <div>
                    <label className="text-xs font-medium text-gray-500">Nom de l'organisation</label>
                    <p className="text-sm text-gray-900">{organization.name}</p>
                  </div>
                  {organization.description && (
                    <div>
                      <label className="text-xs font-medium text-gray-500">Description</label>
                      <p className="text-sm text-gray-900">{organization.description}</p>
                    </div>
                  )}
                </div>
              </div>
              
              <div>
                <div className="space-y-3">
                  <div>
                    <label className="text-xs font-medium text-gray-500">Statut d'abonnement</label>
                    <div className="mt-1">
                      <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                        organization.is_subscribed 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-red-100 text-red-700'
                      }`}>
                        {organization.is_subscribed ? 'Abonné' : 'Non abonné'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Audit Program Card */}
        {audit_program && (
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center mb-4">
              <ClipboardList className="h-5 w-5 text-gray-600 mr-2" />
              <h2 className="text-sm font-semibold text-gray-900">Programme d'audit</h2>
            </div>
            
            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium text-gray-500">Titre</label>
                <p className="text-sm text-gray-900">{audit_program.title}</p>
              </div>
              {audit_program.objective && (
                <div>
                  <label className="text-xs font-medium text-gray-500">Objectif</label>
                  <p className="text-sm text-gray-900">{audit_program.objective}</p>
                </div>
              )}
              <div>
                <label className="text-xs font-medium text-gray-500">Statut</label>
                <div className="mt-1">
                  <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                    audit_program.status === 'opened' ? 'bg-blue-100 text-blue-700' :
                    audit_program.status === 'closed' ? 'bg-red-100 text-red-700' :
                    audit_program.status === 'achieved' ? 'bg-green-100 text-green-700' :
                    'bg-gray-100 text-gray-700'
                  }`}>
                    {audit_program.status === 'opened' ? 'Ouvert' :
                     audit_program.status === 'closed' ? 'Fermé' :
                     audit_program.status === 'achieved' ? 'Réalisé' : audit_program.status}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Creator and Last Updated By */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {created_by && (
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-center mb-4">
                <User className="h-5 w-5 text-gray-600 mr-2" />
                <h2 className="text-sm font-semibold text-gray-900">Créé par</h2>
              </div>
              
              <div className="space-y-2">
                <p className="text-sm text-gray-900">
                  {created_by.full_name || `${created_by.first_name} ${created_by.last_name}`.trim() || 'Inconnu'}
                </p>
                {created_by.email && (
                  <div className="flex items-center">
                    <Mail className="h-3 w-3 text-gray-400 mr-1" />
                    <p className="text-xs text-gray-600">{created_by.email}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {last_updated_by && (
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-center mb-4">
                <User className="h-5 w-5 text-gray-600 mr-2" />
                <h2 className="text-sm font-semibold text-gray-900">Dernière modification par</h2>
              </div>
              
              <div className="space-y-2">
                <p className="text-sm text-gray-900">
                  {last_updated_by.full_name || 'Inconnu'}
                </p>
                {last_updated_by.email && (
                  <div className="flex items-center">
                    <Mail className="h-3 w-3 text-gray-400 mr-1" />
                    <p className="text-xs text-gray-600">{last_updated_by.email}</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Sections Card */}
        {sections && sections.length > 0 && (
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <ListChecks className="h-5 w-5 text-gray-600 mr-2" />
                <h2 className="text-sm font-semibold text-gray-900">Sections d'audit</h2>
              </div>
              <div className="text-xs text-gray-500">
                {sections.length} section(s)
              </div>
            </div>

            <div className="space-y-3">
              {sections.map((section) => (
                <div key={section.id} className="p-4 rounded-lg border border-gray-200 bg-gray-50">
                  <p className="text-sm font-medium text-gray-900 mb-1">{section.section_name}</p>
                  {section.location && (
                    <p className="text-xs text-gray-600 mb-2">Localisation: {section.location}</p>
                  )}
                  <div className="flex items-center space-x-3 text-xs text-gray-500">
                    <span>{new Date(section.created_at).toLocaleDateString('fr-FR')}</span>
                    {section.created_by && (
                      <>
                        <span>•</span>
                        <span>Créé par: {section.created_by.name || section.created_by.email}</span>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Attachments Card */}
        {planData.attachments && planData.attachments.length > 0 && (
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <FileText className="h-5 w-5 text-gray-600 mr-2" />
                <h2 className="text-sm font-semibold text-gray-900">Pièces jointes</h2>
              </div>
              <div className="text-xs text-gray-500">
                {planData.attachments.length} fichier(s)
              </div>
            </div>

            <div className="space-y-3">
              {planData.attachments.map((attachment) => (
                <div key={attachment.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="flex items-center space-x-3">
                    <FileText className="h-5 w-5 text-green-600" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">{attachment.file_name}</p>
                      <p className="text-xs text-gray-500">
                        {attachment.file_size && `${(attachment.file_size / 1024).toFixed(2)} KB • `}
                        Téléchargé le {new Date(attachment.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <button 
                    onClick={() => handleDownloadAttachment(attachment.id, attachment.file_name)}
                    className="inline-flex items-center px-3 py-1 text-sm text-green-600 hover:text-green-800 border border-green-300 rounded hover:bg-green-50 transition-colors"
                  >
                    <Download className="h-4 w-4 mr-1" />
                    Télécharger
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Risks Card */}
        {risks && risks.length > 0 && (
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <AlertTriangle className="h-5 w-5 text-gray-600 mr-2" />
                <h2 className="text-sm font-semibold text-gray-900">Risques du plan d'audit</h2>
              </div>
              <div className="text-xs text-gray-500">
                {risks.length} risque(s)
              </div>
            </div>

            <div className="space-y-4">
              {risks.map((risk) => (
                <div key={risk.id} className="p-4 rounded-lg border border-gray-200 bg-gray-50">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900 mb-1">{risk.risk_name}</p>
                      {risk.description && (
                        <p className="text-xs text-gray-600 mb-2">{risk.description}</p>
                      )}
                      <div className="flex items-center space-x-3 text-xs mb-2">
                        <span className={`inline-block px-2 py-0.5 rounded font-medium ${
                          risk.probability === 'critical' ? 'bg-red-100 text-red-700' :
                          risk.probability === 'high' ? 'bg-orange-100 text-orange-700' :
                          risk.probability === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                          risk.probability === 'low' ? 'bg-green-100 text-green-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          Probabilité: {risk.probability === 'critical' ? 'Critique' :
                                       risk.probability === 'high' ? 'Élevée' :
                                       risk.probability === 'medium' ? 'Moyenne' :
                                       risk.probability === 'low' ? 'Faible' : risk.probability}
                        </span>
                        <span className="text-gray-500">
                          Date: {new Date(risk.risk_date).toLocaleDateString('fr-FR')}
                        </span>
                      </div>
                      {risk.consequence && (
                        <p className="text-xs text-gray-600 mb-2">
                          <span className="font-medium">Conséquence:</span> {risk.consequence}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Risk Actions */}
                  {risk.actions && risk.actions.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <div className="flex items-center mb-2">
                        <CheckCircle2 className="h-4 w-4 text-gray-600 mr-1" />
                        <h4 className="text-xs font-semibold text-gray-900">Actions ({risk.actions.length})</h4>
                      </div>
                      <div className="space-y-2">
                        {risk.actions.map((action) => (
                          <div key={action.id} className="p-3 rounded border border-gray-200 bg-white">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <p className="text-xs text-gray-900 mb-1">{action.action}</p>
                                <div className="flex items-center space-x-3 text-xs text-gray-500">
                                  <span className={`inline-block px-2 py-0.5 rounded font-medium ${
                                    action.is_completed 
                                      ? 'bg-green-100 text-green-700' 
                                      : 'bg-yellow-100 text-yellow-700'
                                  }`}>
                                    {action.is_completed ? 'Terminée' : 'En cours'}
                                  </span>
                                  {action.due_date && (
                                    <>
                                      <span>•</span>
                                      <span>Échéance: {new Date(action.due_date).toLocaleDateString('fr-FR')}</span>
                                    </>
                                  )}
                                </div>
                                {action.user_assigned && (
                                  <p className="text-xs text-gray-600 mt-1">
                                    Assigné à: {action.user_assigned.full_name || action.user_assigned.email}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Statistics Card */}
        {statistics && (
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center mb-4">
              <ClipboardList className="h-5 w-5 text-gray-600 mr-2" />
              <h2 className="text-sm font-semibold text-gray-900">Statistiques</h2>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center p-3 bg-gray-50 rounded-lg border border-gray-200">
                <p className="text-2xl font-bold text-gray-900">{statistics.total_parties}</p>
                <p className="text-xs text-gray-500 mt-1">Parties prenantes</p>
              </div>
              <div className="text-center p-3 bg-gray-50 rounded-lg border border-gray-200">
                <p className="text-2xl font-bold text-gray-900">{statistics.total_sections}</p>
                <p className="text-xs text-gray-500 mt-1">Sections</p>
              </div>
              <div className="text-center p-3 bg-gray-50 rounded-lg border border-gray-200">
                <p className="text-2xl font-bold text-gray-900">{statistics.total_risks}</p>
                <p className="text-xs text-gray-500 mt-1">Risques</p>
              </div>
              <div className="text-center p-3 bg-gray-50 rounded-lg border border-gray-200">
                <p className="text-2xl font-bold text-gray-900">{statistics.total_risk_actions}</p>
                <p className="text-xs text-gray-500 mt-1">Actions de risque</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
